/* eslint-disable no-console */
import { Codes, type Dto } from '@lmc/ts-common/lib/utils/errors'
import * as Sentry from '@sentry/react'
import CONSTANTS from 'utils/constants'
import { getQueryParams } from 'utils/getQueryParams'
import Actions from '../../actions'
import store from '../../store'
import {
  getHomeChangedModalOpen,
  setHomeChangedModalOpen
} from './homeSwitchBroadcast'

const isBrowserEnv = (): boolean => {
  if (window) {
    return true
  }

  return false
}

const checkIfBrowser = () => {
  const isBrowser: boolean = isBrowserEnv()

  if (!isBrowser) {
    throw new Error('This is not a browser env')
  }
}

const ssoLogin = async () => {
  const redirectUri = `${window.location.origin}${window.location.pathname}`

  const params = {
    client_id: CONSTANTS.VITE_FUSIONAUTH_APP_CO_CLIENT_ID,
    redirectUri,
    scope: 'offline_access'
  }

  const queryParams = getQueryParams(params)

  checkIfBrowser()

  window.location.href = `${CONSTANTS.VITE_API_CM_URL_PROXY}/v5/auth/login?${queryParams}`
}

export const ssoLogout = async () => {
  Sentry.setUser(null)

  checkIfBrowser()

  window.location.href = `${
    CONSTANTS.VITE_APP_CM_API
  }/auth/logout?${new URLSearchParams({
    client_id: CONSTANTS.VITE_FUSIONAUTH_APP_CO_CLIENT_ID
  })}`
}

export const ssoMiddleware = async (response: Response) => {
  if (response.status === 401) {
    await ssoLogin()
  }
}

export const resetHomeChangedModalGuard = () => {
  setHomeChangedModalOpen(false)
}

export class HomeChangedError extends Error {}

/** The app's start-up request. App owns its failures. */
export const APP_INIT_URL = '/init'

const ACCESS_RELOAD_KEY = 'careOfficeAccessReloadAt'
const ACCESS_RELOAD_INTERVAL_MS = 60_000

export const isCareOfficeAccessDenied = (status: number, body: unknown) =>
  status === 403 &&
  (body as { code?: unknown } | null)?.code ===
    Codes.onlySeniorCarersAndAdminsHaveAccessToTheCo

/**
 * A role that loses Care Office access mid-session (a downgrade, or a session
 * that predates the backend check) gets this 403 on every request. Reloading
 * hands over to App, whose /init gets the same answer and shows the no-access
 * screen. /init itself is left to App, or the reload would repeat forever.
 *
 * The two backend auth paths cache the user separately, so /init can briefly
 * succeed while other requests are refused. Reloading at most once a minute
 * stops that from becoming a reload loop.
 */
export const careOfficeAccessMiddleware = async (
  response: Response,
  url: string
) => {
  if (response.status !== 403 || url === APP_INIT_URL) return

  let body: unknown
  try {
    body = await response.clone().json()
  } catch {
    return
  }
  if (!isCareOfficeAccessDenied(response.status, body)) return

  try {
    const lastReload = Number(window.sessionStorage.getItem(ACCESS_RELOAD_KEY))
    const sinceLastReload = Date.now() - lastReload
    if (sinceLastReload >= 0 && sinceLastReload < ACCESS_RELOAD_INTERVAL_MS) {
      return
    }
    window.sessionStorage.setItem(ACCESS_RELOAD_KEY, String(Date.now()))
  } catch {
    // Storage blocked: reload anyway rather than leave the user on broken screens.
  }

  window.location.reload()
}

export const homeChangedMiddleware = async (response: Response) => {
  if (response.status !== 409) return

  let body: any
  try {
    body = await response.clone().json()
  } catch {
    return
  }
  if (body?.code !== 'HOME_CHANGED') return

  if (getHomeChangedModalOpen()) throw new HomeChangedError()
  setHomeChangedModalOpen(true)

  const action = Actions.openModal({
    modalId: 'homeChangedModal',
    data: {
      currentHomeName: body.currentHome?.name,
      requestedHomeId: body.requestedHome?.id
    }
  })
  // @ts-ignore untyped Redux store
  store.dispatch(action)

  throw new HomeChangedError()
}

export const checkMFAForce = async (response: Dto) => {
  if (
    response.code === Codes.mfaIsMissingWhenFeatureIsEnabled &&
    window.location.pathname !== '/mfa'
  ) {
    window.location.replace('/mfa')
  }
}
